import {Component, OnInit, ViewChild} from '@angular/core';
import {
  AchField,
  BankAccountHolderType,
  MerchantCategoryCodeRule,
  MerchantProfile,
  MerchantProfileCategory,
  MerchantProfileContact,
  MerchantProfileContactField,
  MerchantProfileContactType,
  MerchantProfileFormValue,
  MerchantProfilePayload,
  MerchantProfileSection,
  MerchantProfileStatus,
  MerchantProfileStatusLabels,
  Pager,
  PaymentSource,
  ProcessorConfig,
  ProfileTab,
  ProfileTabLabels,
  Region,
  StripeAccountType,
  Timezones,
  User,
} from '../_models';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {CrudSaveComponent} from '../_directives';
import {Location} from '@angular/common';
import {
  AlertService,
  BankAccountConnectionService,
  CampaignService,
  MerchantCategoryService,
  MerchantProfileService,
  PaymentSourceService,
  RegionService,
  UserService,
} from '../_services';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {copyToClipboard, CustomValidators} from '../_helpers';
import {config} from '../../config/config';
import {MerchantProfilesResolveComponent} from './merchant-profiles-resolve.component';

@Component({
  selector: 'app-merchant-profiles-new',
  templateUrl: './merchant-profiles-edit.component.html',
  styleUrls: [
    './merchant-profiles-edit.component.scss',
    '../checkouts/checkout-profiles/profiles-styles.component.scss',
  ],
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('500ms ease-in', style({ opacity: 1 }))]),
    ]),
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', opacity: 0, display: 'none' })),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('collapsed <=> expanded', animate('250ms ease-in-out')),
    ]),
  ],
})
export class MerchantProfilesNewComponent extends CrudSaveComponent implements OnInit {
  ProfileTabs = ProfileTab;
  activeTab = ProfileTab.Credentials;
  merchantProfileStatus = MerchantProfileStatus;
  merchantProfileContactType = MerchantProfileContactType;
  merchantProfileSection = MerchantProfileSection;
  merchantCategoryCodeRule = MerchantCategoryCodeRule;
  achField = AchField;
  paymentSources: PaymentSource[] = [];
  merchantProfile: MerchantProfile = null;
  currentUser: User = null;
  copiedValue: string | null = null;
  categories: MerchantProfileCategory[] = [];
  availableStates: Region[] = [];
  countries: Region[] = [];
  processorTypes: ProcessorConfig[] = [];

  timezones = Timezones;

  // Collapsible cards on the credentials tab
  sections = {
    merchantProfile: true,
    accountDetails: true,
    contacts: true,
  };

  startedContacts = { technical_contact: false, emergency_contact: false };
  includeResolve = false;
  includePayments = false;
  featureParams: Params = {};

  @ViewChild(MerchantProfilesResolveComponent, { static: false }) resolveComponent: MerchantProfilesResolveComponent;

  useSolvpathCredentials = false;
  usePrimaryAsTechnicalContact = false;
  usePrimaryAsRiskContact = false;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected merchantProfileService: MerchantProfileService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected campaignService: CampaignService,
    protected merchantCategoryService: MerchantCategoryService,
    protected userService: UserService,
    protected regionService: RegionService,
    protected paymentSourceService: PaymentSourceService,
    protected bankAccountConnectionService: BankAccountConnectionService
  ) {
    super(router, location, route, merchantProfileService, alertService);
    this.isNew = true;
    this.objectName = 'merchant profile';
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      name: [null, [Validators.required]],
      description: [null, [Validators.required]],
      category: [null, [Validators.required]],
      url: [null],
      address: this.formBuilder.group({
        address1: [null, [Validators.required]],
        address2: [null],
        city: [null, [Validators.required]],
        state: [null],
        zip: [null, [Validators.required]],
        country: ['us', [Validators.required]],
      }),
      business_contact: this.buildContactGroup(),
      technical_contact: this.buildContactGroup(true),
      emergency_contact: this.buildContactGroup(true),
      merchant_category_code: [null, [Validators.required, ...CustomValidators.formatRule(MerchantCategoryCodeRule)]],
    });

    super.ngOnInit();

    if (this.route.snapshot.queryParams['resolve']) {
      this.includeResolve = true;
      this.featureParams.resolve = this.route.snapshot.queryParams['resolve'];
    }

    if (this.route.snapshot.queryParams['gateway']) {
      this.includePayments = true;
      this.featureParams.gateway = this.route.snapshot.queryParams['gateway'];
      this.loadPaymentSources();
      this.form.addControl('payment_source', new FormControl(null));
    }

    this.loadCategories();
    this.loadCountries();
    this.loadStates();
    this.watchOptionalContact('technical_contact');
    this.watchOptionalContact('emergency_contact');

    this.userService.getCurrent().subscribe(
      (user: User) => {
        this.currentUser = user;
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  protected applyCategoryFromRoute() {
    const categoryId = this.route.snapshot.queryParams['category'];

    if (categoryId && !this.id) {
      const category = this.categories.find((item) => String(item.id) === String(categoryId));

      if (category) {
        this.form.patchValue({ category: category.id });
      }
    }
  }

  // The API takes a contact whole or not at all, so an optional one gets validators only once started
  protected buildContactGroup(optional: boolean = false) {
    const group = this.formBuilder.group({
      first_name: [null, optional ? [] : [Validators.required]],
      last_name: [null, optional ? [] : [Validators.required]],
      email: [null, optional ? [Validators.email] : [Validators.required, Validators.email]],
      phone: [null, optional ? [] : [Validators.required]],
    });

    return group;
  }

  protected watchOptionalContact(name: string) {
    const group = this.form.get(name) as FormGroup;

    group.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      const started = this.hasContactValue(group.value);

      // Only when the contact crosses between empty and started, so the update cannot feed itself
      if (started !== this.startedContacts[name]) {
        this.startedContacts[name] = started;
        this.updateOptionalContactValidators(group);
      }
    });
  }

  protected updateOptionalContactValidators(group: FormGroup) {
    const started = this.hasContactValue(group.value);

    Object.keys(group.controls).forEach((name) => {
      const control = group.controls[name];
      const validators = [];

      if (started) {
        validators.push(Validators.required);
      }

      if (name === MerchantProfileContactField.Email) {
        validators.push(Validators.email);
      }

      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    });

    // The controls were updated on their own, so the group still carries the validity it had
    group.updateValueAndValidity({ emitEvent: false });
  }

  // patchValue can't take a null group, so null contacts fall back to an empty shape
  protected toFormValue(profile: MerchantProfile): MerchantProfileFormValue {
    const emptyContact: MerchantProfileContact = { first_name: null, last_name: null, email: null, phone: null };

    return {
      ...profile,
      address: profile.address ? { ...profile.address, country: this.toCountryId(profile.address.country) } : profile.address,
      technical_contact: profile.technical_contact ? profile.technical_contact : emptyContact,
      emergency_contact: profile.emergency_contact ? profile.emergency_contact : emptyContact,
    };
  }

  protected toCountryId(country: string): string {
    return country ? country.toLowerCase() : 'us';
  }

  protected getFormData(): MerchantProfilePayload {
    const value: MerchantProfileFormValue = this.form.value;
    const data: MerchantProfilePayload = { ...value };

    if (this.isNew) {
      data.campaign = this.campaignService.getCurrentCampaignId();
    }

    // An emptied optional contact is sent as null to clear it; omitting the key keeps the stored one
    if (!this.hasContactValue(value.technical_contact)) {
      data.technical_contact = null;
    }

    if (!this.hasContactValue(value.emergency_contact)) {
      data.emergency_contact = null;
    }

    return data;
  }

  protected hasContactValue(contact: MerchantProfileContact): boolean {
    let filled = false;

    if (contact) {
      filled = [contact.first_name, contact.last_name, contact.email, contact.phone].some(
        (field) => field !== null && field !== undefined && String(field).length > 0
      );
    }

    return filled;
  }

  protected applyProfile(profile: MerchantProfile) {
    this.merchantProfile = profile;
    this.form.patchValue(this.toFormValue(profile));
    this.loadStates();
    this.updateConditionalValidators();
  }

  protected reloadProfile(message: string) {
    this.merchantProfileService
      .get(this.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (profile: MerchantProfile) => {
          this.applyProfile(profile);
          this.form.markAsPristine();
          this.canDeactivateService.setFormDirty(false);
          this.submitted = false;
          this.alertService.success(message);
          this.loading = false;
        },
        (error) => {
          this.handleError(error);
          this.loading = false;
        }
      );
  }

  setActiveTab(tab: ProfileTab) {
    this.activeTab = tab;
    this.canDeactivateService.setFormDirty(this.form.dirty);
  }

  getTabLabel(tab: ProfileTab): string {
    return ProfileTabLabels[tab] || '';
  }

  isExpanded(section: MerchantProfileSection): boolean {
    return this.sections[section];
  }

  toggleSection(section: MerchantProfileSection) {
    this.sections[section] = !this.sections[section];
  }

  protected updateConditionalValidators() {
    ['technical_contact', 'emergency_contact'].forEach((name) => {
      const group = this.form.get(name) as FormGroup;
      this.startedContacts[name] = this.hasContactValue(group.value);
      this.updateOptionalContactValidators(group);
    });
  }

  isContactStarted(name: string): boolean {
    let started = false;

    if (this.startedContacts[name]) {
      started = true;
    }

    return started;
  }

  onCredentialsChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.useSolvpathCredentials = input.checked;

    const businessGroup = this.form.get('business_contact');
    const addressGroup = this.form.get('address');

    if (input.checked && this.currentUser) {
      businessGroup.patchValue({
        first_name: this.currentUser.first_name,
        last_name: this.currentUser.last_name,
        email: this.currentUser.email,
        phone: this.currentUser.phone,
      });

      if (this.currentUser.account && this.currentUser.account.default_address) {
        const address = this.currentUser.account.default_address;
        addressGroup.patchValue({
          address1: address.address1,
          address2: address.address2,
          city: address.city,
          state: address.state,
          zip: address.zip,
          country: this.toCountryId(address.country),
        });
        this.loadStates();
      }
    } else if (!input.checked) {
      businessGroup.reset();
      addressGroup.reset({ country: 'us' });
      this.loadStates();
    }

    this.markEdited([businessGroup, addressGroup]);
    this.form.updateValueAndValidity();
  }

  // These switches change no value or reset one, so mark dirty and tell the leave guard directly
  protected markEdited(controls: AbstractControl[]) {
    controls.forEach((control) => {
      control.markAsDirty();
    });
    this.canDeactivateService.setFormDirty(true);
  }

  onContactChange(event: Event, contactType: MerchantProfileContactType) {
    const input = event.target as HTMLInputElement;
    const isTechnical = contactType === MerchantProfileContactType.Technical;
    const targetGroup = isTechnical ? this.form.get('technical_contact') : this.form.get('emergency_contact');

    if (isTechnical) {
      this.usePrimaryAsTechnicalContact = input.checked;
    } else {
      this.usePrimaryAsRiskContact = input.checked;
    }

    if (input.checked) {
      targetGroup.patchValue(this.form.get('business_contact').value);
    } else {
      targetGroup.reset();
    }

    this.markEdited([targetGroup]);
  }

  copyValue(value: string) {
    copyToClipboard(value);
    this.copiedValue = value;

    setTimeout(() => {
      this.copiedValue = null;
    }, 1000);
  }

  getStatusLabel(status: MerchantProfileStatus) {
    return MerchantProfileStatusLabels[status];
  }

  onSubmit() {
    this.submitProfile();
  }

  protected loadCountries() {
    this.regionService
      .getCountries()
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (countries: Region[]) => {
          this.countries = countries;
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  protected loadStates() {
    this.regionService
      .list(this.form.get('address.country').value)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (states: Region[]) => {
          this.availableStates = states ? states : [];
          this.setRequired('address.state', this.availableStates.length > 0);
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  onCountryChange() {
    this.form.get('address.state').setValue(null);
    this.loadStates();
  }

  protected loadPaymentSources() {
    this.paymentSourceService
      .list({ page: 1, page_size: config.maxPageSize, stripe_account_type: StripeAccountType.Gateway })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (page: Pager) => {
          this.paymentSources = page && page.results ? page.results : [];
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  getSelectedPaymentSource(): PaymentSource {
    const id = this.form.get('payment_source').value;
    const source = this.paymentSources.find((item) => item.id === id);

    return source ? source : null;
  }

  // Empty when the selected source has no value, so the row is left out
  getAchValue(field: AchField): string {
    const source = this.getSelectedPaymentSource();
    let text = '';

    if (source) {
      if (field === AchField.AccountNumber && source.last4) {
        text = '****' + source.last4;
      } else if (field === AchField.Name && source.name) {
        text = source.name;
      } else if (field === AchField.AccountHolderType && source.account_holder_type) {
        text = source.account_holder_type === BankAccountHolderType.Business ? 'Business' : 'Personal';
      }
    }

    return text;
  }

  addPaymentMethod() {
    this.loading = true;

    this.bankAccountConnectionService.connectBankAccount(StripeAccountType.Gateway).subscribe(
      (source: PaymentSource) => {
        this.alertService.success('Added New Bank Account.', true);
        this.loading = false;
        this.paymentSources.push(source);
        this.form.get('payment_source').markAsDirty();
        this.form.get('payment_source').setValue(source.id);
      },
      (error) => {
        this.loading = false;

        if (error.cancelled) {
          // The user closed the modal without connecting an account
        } else if (error.status === 400 && error.error && 'token' in error.error) {
          this.alertService.error(error.error.token);
        } else if (error.message) {
          this.alertService.error(error.message);
        } else {
          this.handleError(error);
        }
      }
    );
  }

  protected loadCategories() {
    this.merchantCategoryService.list().subscribe(
      (categories: MerchantProfileCategory[]) => {
        this.categories = categories;
        this.updateConditionalValidators();
        this.applyCategoryFromRoute();
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  // An existing profile renders only once loaded, so the empty create form never flashes
  get profileReady(): boolean {
    return this.isNew || !!this.merchantProfile;
  }

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  get resolveHasChanges(): boolean {
    return !!(this.resolveComponent && this.resolveComponent.form && this.resolveComponent.form.dirty);
  }

  protected submitProfile() {
    if (this.hasChanges) {
      this.validateAndSave();
    }
  }

  protected validateAndSave() {
    this.submitted = true;
    this.updateConditionalValidators();
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.loading = true;
      const payload = this.getFormData();
      // PATCH, not PUT: without the optional contacts no PUT body passes (they reject omission and null)
      const request = this.isNew
        ? this.merchantProfileService.create(payload)
        : this.merchantProfileService.patch(this.id, payload);

      request.pipe(takeUntil(this._destroy$)).subscribe(
        () => {
          this.onProfileSaved('Merchant profile saved.');
        },
        (error) => {
          this.handleSubmitError(error);
          this.loading = false;
        }
      );
    } else {
      this.activeTab = ProfileTab.Credentials;
    }
  }

  protected onProfileSaved(message: string) {
    if (this.isNew) {
      this.form.markAsPristine();
      this.canDeactivateService.setFormDirty(false);
      this.alertService.success(message, true);
      this.loading = false;
      this.router.navigate(['/merchant-profiles'], { queryParams: this.featureParams });
    } else {
      this.reloadProfile(message);
    }
  }
}
